import api from './api';

export const loanService = {
  apply: (data) => api.post('/loans/apply/', data),
  getProducts: () => api.get('/loans/products/'),
  getMyLoans: (params) => api.get('/loans/', { params }),
  getLoan: (id) => api.get(`/loans/${id}/`),
  acceptOffer: (id) => api.post(`/loans/${id}/accept-offer/`),
  declineOffer: (id) => api.post(`/loans/${id}/decline-offer/`),
  getRepaymentSchedule: (loanId) => api.get(`/repayments/${loanId}/schedule/`),
  makePayment: (loanId, data) => api.post(`/repayments/${loanId}/pay/`, data),
  getPaymentHistory: (loanId) => api.get(`/repayments/${loanId}/history/`),
  previewSchedule: (data) => api.post('/loans/preview-schedule/', data),
};

export const kycService = {
  submit: (data) => api.post('/kyc/submit/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getStatus: () => api.get('/kyc/status/'),
};

export const documentService = {
  upload: (data) => api.post('/documents/upload/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMyDocuments: (params) => api.get('/documents/', { params }),
  deleteDocument: (id) => api.delete(`/documents/${id}/`),
  getMyDocumentRequests: (params) => api.get('/documents/my-requests/', { params }),
  getLoanDocumentRequests: (loanId) => api.get(`/documents/loans/${loanId}/requests/`),
  fulfillRequest: (requestId, formData) => api.post(`/documents/requests/${requestId}/fulfill/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reviewDocument: (docId, data) => api.post(`/documents/admin/${docId}/review/`, data),
  waiveRequest: (requestId) => api.post(`/documents/admin/requests/${requestId}/waive/`),
};

export const notificationService = {
  getAll: (params) => api.get('/notifications/', { params }),
  markRead: (id) => api.post(`/notifications/${id}/read/`),
  markAllRead: () => api.post('/notifications/mark-all-read/'),
};

export const adminService = {
  // Dashboard & Reports
  getDashboardStats: () => api.get('/admin-portal/dashboard/'),
  getReports: () => api.get('/admin-portal/reports/'),

  // Users
  getUsers: (params) => api.get('/admin-portal/users/', { params }),
  getUser: (id) => api.get(`/admin-portal/users/${id}/`),
  updateUserStatus: (id, data) => api.patch(`/admin-portal/users/${id}/status/`, data),
  setReviewerType: (id, reviewer_type) => api.patch(`/admin-portal/users/${id}/reviewer-type/`, { reviewer_type }),
  assignRole: (userId, data) => api.post(`/admin-portal/users/${userId}/roles/`, data),
  revokeRole: (userId, roleId) => api.delete(`/admin-portal/users/${userId}/roles/${roleId}/revoke/`),

  // RBAC
  getRoles: () => api.get('/admin-portal/roles/'),
  getPermissions: (params) => api.get('/admin-portal/permissions/', { params }),

  // Approvals
  getApprovals: (params) => api.get('/admin-portal/approvals/', { params }),
  getApproval: (id) => api.get(`/admin-portal/approvals/${id}/`),
  decideApproval: (id, data) => api.post(`/admin-portal/approvals/${id}/decide/`, data),
  cancelApproval: (id) => api.post(`/admin-portal/approvals/${id}/cancel/`),

  // Loans
  getAllLoans: (params) => api.get('/admin-portal/loans/', { params }),
  loanAction: (id, data) => api.post(`/admin-portal/loans/${id}/action/`, data),

  // KYC
  getAllKYC: (params) => api.get('/admin-portal/kyc/', { params }),
  getKYCDetail: (id) => api.get(`/admin-portal/kyc/${id}/review/`),
  reviewKYC: (id, data) => api.post(`/admin-portal/kyc/${id}/review/`, data),
  kycRequestDocuments: (id, data) => api.post(`/admin-portal/kyc/${id}/request-docs/`, data),
  getKYCDocumentRequests: (kycId) => api.get(`/documents/kyc/${kycId}/requests/`),
  fulfillKYCRequest: (requestId, formData) => api.post(`/documents/kyc-requests/${requestId}/fulfill/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  waiveKYCRequest: (requestId) => api.post(`/documents/kyc-requests/${requestId}/waive/`),

  // Disbursements
  getDisbursements: (params) => api.get('/admin-portal/disbursements/', { params }),
  prepareDisbursement: (data) => api.post('/admin-portal/disbursements/prepare/', data),
  executeDisbursement: (id) => api.post(`/admin-portal/disbursements/${id}/execute/`),

  // Disbursement Officer
  getDisbursementQueue: (type = 'pending') => api.get('/admin-portal/disbursements/queue/', { params: { type } }),
  getDisbursementLoanDetail: (loanId) => api.get(`/admin-portal/disbursements/${loanId}/loan/`),
  autoDisburse: (loanId) => api.post(`/admin-portal/disbursements/${loanId}/auto/`),
  manualDisburse: (loanId, formData) => api.post(`/admin-portal/disbursements/${loanId}/manual/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Refunds
  getRefunds: (params) => api.get('/admin-portal/refunds/', { params }),
  initiateRefund: (data) => api.post('/admin-portal/refunds/initiate/', data),
  decideRefund: (id, data) => api.post(`/admin-portal/refunds/${id}/decide/`, data),

  // Fraud
  getFraudAlerts: (params) => api.get('/admin-portal/fraud/', { params }),
  raiseFraudAlert: (data) => api.post('/admin-portal/fraud/raise/', data),
  updateFraudAlert: (id, data) => api.patch(`/admin-portal/fraud/${id}/`, data),

  // API Clients
  getApiClients: (params) => api.get('/admin-portal/api-clients/', { params }),
  createApiClient: (data) => api.post('/admin-portal/api-clients/create/', data),
  updateApiClientStatus: (id, data) => api.patch(`/admin-portal/api-clients/${id}/status/`, data),

  // Audit Logs
  getAuditLogs: (params) => api.get('/admin-portal/audit-logs/', { params }),
  getAuditStats: () => api.get('/admin-portal/audit-logs/stats/'),

  // Repayments (admin)
  getLoanSchedule: (loanId) => api.get(`/repayments/admin/${loanId}/schedule/`),
  getLoanPayments: (loanId) => api.get(`/repayments/admin/${loanId}/payments/`),
  recordPayment: (loanId, scheduleId, data) => api.post(`/repayments/admin/${loanId}/schedule/${scheduleId}/record/`, data),
  confirmPayment: (loanId, paymentId, decision = 'confirm') => api.post(`/repayments/admin/${loanId}/payments/${paymentId}/confirm/`, { decision }),
  makerReviewPayment: (loanId, paymentId, data) => api.post(`/repayments/admin/${loanId}/payments/${paymentId}/maker-review/`, data),
  getLoanPaymentsPending: (loanId) => api.get(`/repayments/admin/${loanId}/payments/`),
  getPendingMakerPayments: () => api.get('/repayments/admin/queue/pending-maker/'),
  getPendingCheckerPayments: () => api.get('/repayments/admin/queue/pending-checker/'),

  // Loan application detail (admin)
  getAdminLoan: (id) => api.get(`/loans/admin/${id}/`),

  // Bureau credit check
  runBureauCreditCheck: (loanId, reportType) =>
    api.post(`/loans/admin/${loanId}/bureau-credit-check/`, { report_type: reportType }),
  getBureauCreditCheck: (loanId) =>
    api.get(`/loans/admin/${loanId}/bureau-credit-check/`),

  // Loan Products
  getLoanProducts: (params) => api.get('/loans/admin/products/', { params }),
  createLoanProduct: (data) => api.post('/loans/admin/products/', data),
  updateLoanProduct: (id, data) => api.patch(`/loans/admin/products/${id}/`, data),
  deactivateLoanProduct: (id) => api.delete(`/loans/admin/products/${id}/`),
  setTierDiscounts: (id, data) => api.post(`/loans/admin/products/${id}/discounts/`, data),

  // Analytics
  getUserAnalytics: () => api.get('/admin-portal/analytics/users/'),
  getLoanAnalytics: () => api.get('/admin-portal/analytics/loans/'),
  getNplAnalytics: () => api.get('/admin-portal/analytics/npl/'),

  // Reports
  getCreditBureauReport: (month) => api.get('/admin-portal/reports/credit-bureau/', { params: month ? { month } : {} }),
  getManagementReport: (month) => api.get('/admin-portal/reports/management/', { params: month ? { month } : {} }),
};

export const supportService = {
  getTickets: (params) => api.get('/support/', { params }),
  getTicket: (id) => api.get(`/support/${id}/`),
  createTicket: (data) => api.post('/support/', data),
  addMessage: (id, data) => api.post(`/support/${id}/message/`, data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
  }),
  updateStatus: (id, status) => api.patch(`/support/${id}/status/`, { status }),
};
